import { Grid, Link as TableLink, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FilterList } from 'src/components/filterList';
import { Footer } from 'src/components/footer/Footer';
import { Header } from 'src/components/header';
import { InputTextLabel } from 'src/components/input/Input.styled';
import { SimpleDialog } from 'src/components/modal';
import { Snackbar } from 'src/components/snackbar';
import MainPane from 'src/components/TwoPaneLayout/MainPane';
import SidePane from 'src/components/TwoPaneLayout/SidePane';
import TwoPaneLayout from 'src/components/TwoPaneLayout/TwoPaneLayout';
import { ViewOnlyAlert } from 'src/components/viewOnlyAlert';
import { PATHS } from 'src/constants/paths';
import { useFilterAndSorting, useModal } from 'src/hooks';
import { useSearchFilter } from 'src/hooks/useSearchFilter';
import { IFilter, ILocation } from 'src/interfaces';
import { lang, useUserRole } from 'src/utils';

import { LocationList } from '../listLocationTagsPage/modules/locationList/LocationList';
import { tagNameMaxLength, TestIds } from './constants';
import {
  InputContainer,
  InputTextField,
  StyleDivider,
} from './EditLocationTagPage.styled';
import { useEditLocationTagPage } from './hooks/EditLocationTagPage.hook';
import { useTagLikeEditDirtyState } from './hooks/useTagLikeEditDirtyState';
import { buildLocationTagAssignmentUpdates } from './modules/EditLocationTagPage.helper';

export const EditLocationTagPage = () => {
  const { tagId = '' } = useParams();
  const [inputBoxStatus, setInputBoxStatus] = useState('');
  const [locationTag, setLocationTag] = useState('');
  const [locations, setLocations] = useState<ILocation[]>([]);
  // Holds a map that tracks the checked/unchecked ones despite the filters are applied
  const [delta, setDelta] = useState<Map<number | string, number>>(
    () => new Map<number | string, number>(),
  );

  const {
    locationsBase,
    fullTaggedServerLocations,
    isLoadingLocations,
    activeFilters,
    updateLocationTag,
    updateLocations,
    selectedLocationTag,
    totalPages,
    totalCount,
    page,
    handlePageChange,
    getParamsValue,
    locationsCount,
  } = useEditLocationTagPage(Number(tagId));

  const tagNum = Number(tagId);

  const locationHasCurrentTag = useCallback(
    (loc: ILocation) => loc.location_tag_id?.includes(tagNum) ?? false,
    [tagNum],
  );

  const persistedWithTag = useMemo(
    () => locationsBase.filter(locationHasCurrentTag),
    [locationsBase, locationHasCurrentTag],
  );

  const tagWideBaseline = useMemo<ILocation[]>(
    () =>
      fullTaggedServerLocations.length > 0
        ? fullTaggedServerLocations
        : persistedWithTag,
    [fullTaggedServerLocations, persistedWithTag],
  );

  const viewLocations = useMemo(
    () =>
      locationsBase.map((locationBase) => {
        const changedLocation = locations.find(
          (location) => location.location_code === locationBase.location_code,
        );

        return changedLocation ?? locationBase;
      }),
    [locationsBase, locations],
  );

  const accumulatedSelectionWithTag = useMemo<ILocation[]>(() => {
    const currentWithTag = viewLocations.filter(locationHasCurrentTag);

    if (fullTaggedServerLocations.length === 0) {
      return currentWithTag;
    }

    const byCode = new Map<string, ILocation>();
    for (const loc of fullTaggedServerLocations) {
      byCode.set(loc.location_code, loc);
    }
    for (const loc of locations) {
      if (loc.location_tag_id?.includes(tagNum)) {
        byCode.set(loc.location_code, loc);
      } else {
        byCode.delete(loc.location_code);
      }
    }

    return Array.from(byCode.values());
  }, [
    fullTaggedServerLocations,
    locationHasCurrentTag,
    locations,
    tagNum,
    viewLocations,
  ]);

  // Sidebar / search filter changes (i.e. anything in the URL except `page`)
  // re-scope the tagged-set query, so the user's pre-filter toggle deltas no
  // longer have a coherent baseline to diff against. Drop the local delta to
  // avoid sticky selections that the user can no longer see.
  const lastFiltersSignatureRef = useRef<string | null>(null);
  useEffect(() => {
    const filtersSignature = JSON.stringify(
      Object.entries(activeFilters).sort(([a], [b]) => a.localeCompare(b)),
    );

    if (lastFiltersSignatureRef.current === null) {
      lastFiltersSignatureRef.current = filtersSignature;

      return;
    }

    if (filtersSignature !== lastFiltersSignatureRef.current) {
      lastFiltersSignatureRef.current = filtersSignature;
      // clearing set locations here results in all changes being lost when changing a filter
      // setLocations([]);
      setDelta(new Map());
    }
  }, [activeFilters]);

  const getLocationMemberKey = useCallback(
    (row: ILocation) => row.location_code,
    [],
  );

  const {
    handleTagNameChange,
    hasCheckboxesChanged,
    hasNameChanged,
    isSaveDisabled,
  } = useTagLikeEditDirtyState({
    routeKey: tagId,
    loadedTagName: selectedLocationTag?.name,
    tagName: locationTag,
    setTagName: setLocationTag,
    selectedRows: accumulatedSelectionWithTag,
    persistedRows: tagWideBaseline,
    getMemberKey: getLocationMemberKey,
    inputBoxStatus,
    setInputBoxStatus,
    duplicateNameAlertMessage: 'Location tag already exists',
  });

  const [isFuzzySearch, setIsFuzzySearch] = useState(
    !('name.list' in activeFilters),
  );

  const [isStoreNumberFuzzySearch, setIsStoreNumberFuzzySearch] = useState(
    !('store_number.list' in activeFilters),
  );

  const [locationsSelectedCount, setLocationsSelectedCount] =
    useState<number>(locationsCount);

  useEffect(() => {
    if (locationsCount !== 0) {
      setLocationsSelectedCount(locationsCount);
    }
  }, [locationsCount]);

  const onStoreNumberSearchTypeChange = () => {
    if (isStoreNumberFuzzySearch) {
      setIsStoreNumberFuzzySearch(false);
      onChangeToExactMatch('store_number.regexp', 'store_number.list');
    } else {
      setIsStoreNumberFuzzySearch(true);
      onChangeToFuzzyMatch('store_number.list', 'store_number.regexp');
    }
  };
  const { handleOnChangeFilterKeyword, handleOnRemoveFilterKeyword } =
    useSearchFilter();

  const navigate = useNavigate();
  const { isViewOnly } = useUserRole();

  const filters: IFilter[] = [
    {
      label: lang.locationTagsName,
      labelOverride: lang.locationTags,
      options: [],
      isLoading: false,
      fieldType: 'filterWithSearch',
      badgeOptions: {
        align: 'top',
        color: 'primary',
      },
      customElement: (
        <TableLink
          component="button"
          variant="body2"
          onClick={() => navigate(PATHS.LOCATIONS_TAGS)}>
          {lang.manageTags}
        </TableLink>
      ),
    },
    {
      labelOverride: lang.ownerOperator.ownerOperatorTags,
      label: lang.locationOwnerTagsName,
      options: [],
      isLoading: false,
      fieldType: 'filterWithSearch',
      badgeOptions: {
        align: 'top',
        color: 'success',
      },
    },
    {
      label: lang.zone,
      options: [],
      isLoading: false,
      fieldType: 'filterWithSearch',
      badgeOptions: {
        align: 'top',
        color: 'default',
      },
    },
    {
      label: lang.subZone,
      options: [],
      isLoading: false,
      fieldType: 'filterWithSearch',
      badgeOptions: {
        align: 'top',
        color: 'default',
      },
    },
    {
      labelOverride: 'Store number',
      label: 'store_number',
      isLoading: false,
      options: [],
      fieldType: 'freeSearch',
      hasExactSearch: true,
      isFuzzySearch: isStoreNumberFuzzySearch,
      onSearchTypeChange: onStoreNumberSearchTypeChange,
      filterSearchVal: getParamsValue(
        isStoreNumberFuzzySearch ? 'store_number.regexp' : 'store_number.list',
      ),
      onChangeFilterKeyword: (value: string) =>
        handleOnChangeFilterKeyword(
          value,
          isStoreNumberFuzzySearch
            ? 'store_number.regexp'
            : 'store_number.list',
        ),
      onRemoveFilterKeyword: (value: string) =>
        handleOnRemoveFilterKeyword(
          value,
          isStoreNumberFuzzySearch
            ? 'store_number.regexp'
            : 'store_number.list',
        ),
    },
  ];

  const {
    onChangeKeyword,
    onRemoveKeyword,
    onChangeFilter,
    onChangeParams,
    onReset,
    onChangeToExactMatch,
    onChangeToFuzzyMatch,
  } = useFilterAndSorting(filters, [], isFuzzySearch, setIsFuzzySearch);

  const onResetFilters = () => {
    onReset();
    onChangeParams('store_number.list', []);
    onChangeParams('store_number.regexp', []);
    setIsStoreNumberFuzzySearch(true);
  };

  const onSearchTypeChange = () => {
    if (isFuzzySearch) {
      onChangeToExactMatch('name.regexp', 'name.list');
      setIsFuzzySearch(false);
    } else {
      onChangeToFuzzyMatch('name.list', 'name.regexp');
      setIsFuzzySearch(true);
    }
  };

  const handleSave = async () => {
    const locationTagForSave = locationTag.trim();

    try {
      if (hasNameChanged) {
        const request = {
          locationTagId: tagId,
          locationTag: locationTagForSave,
        };
        await updateLocationTag.mutateAsync(request);
      }
      if (hasCheckboxesChanged) {
        const updates = buildLocationTagAssignmentUpdates(
          accumulatedSelectionWithTag,
          tagWideBaseline,
          tagNum,
          locations,
          delta,
        );

        if (updates.length > 0) {
          await updateLocations.mutateAsync(updates);
        }
      }
      if (hasNameChanged || hasCheckboxesChanged) {
        navigate(PATHS.LOCATIONS_TAGS);
        Snackbar.success(`${locationTagForSave} ${lang.successfullyUpdated}`);
      }
    } catch (error: any) {
      if (error.response?.data?.error?.includes('Duplicate entry')) {
        setInputBoxStatus('Location tag already exists');
      } else {
        Snackbar.error(lang.failedSaveText);
      }
    }
  };

  const handleUpdateLocations = (
    newLocations: ILocation[],
    isDeselectAll: boolean = false,
  ) => {
    const updatedMap = new Map(
      locations.map((loc) => [loc.location_code, loc]),
    );

    for (const newLoc of newLocations) {
      updatedMap.set(newLoc.location_code, newLoc);
    }

    let updatedLocationArray = Array.from(updatedMap.values());

    if (isDeselectAll) {
      // merge all tagged locations
      for (const taggedLoc of fullTaggedServerLocations) {
        if (!updatedMap.has(taggedLoc.location_code)) {
          updatedMap.set(taggedLoc.location_code, taggedLoc);
        }
      }
      // rebuild the array so merged tagged rows are included, then clear tags.
      updatedLocationArray = Array.from(updatedMap.values()).map((loc) => ({
        ...loc,
        location_tag_id: [],
      }));
    }

    setLocations([...updatedLocationArray]);
  };

  const CloseModal = useModal();

  const handleModalConfirm = () => {
    navigate(PATHS.LOCATIONS_TAGS);
  };

  const isSaving = updateLocationTag.isLoading || updateLocations.isLoading;

  return (
    <Grid>
      <Header
        title={`${lang.edit} ${locationTag}`}
        breadcrumbItems={[
          {
            label: lang.locations,
            link: `${PATHS.LOCATIONS}`,
          },
          {
            label: lang.tags,
            link: `${PATHS.LOCATIONS_TAGS}`,
          },
          {
            label: lang.editTag,
          },
        ]}
        actionBar={isViewOnly && <ViewOnlyAlert />}
      />
      <TwoPaneLayout>
        <SidePane>
          <FilterList
            filters={filters}
            onChangeKeyword={onChangeKeyword}
            onRemoveKeyword={onRemoveKeyword}
            onChangeFilter={onChangeFilter}
            showReset
            onReset={onResetFilters}
            hasExactSearch
            onSearchTypeChange={onSearchTypeChange}
            isFuzzySearch={isFuzzySearch}
          />
        </SidePane>
        <MainPane>
          <Typography variant="h4" component="h2" gutterBottom>
            {lang.tagDetails}
          </Typography>
          <StyleDivider />
          <InputContainer>
            <InputTextLabel>{lang.tagName}</InputTextLabel>
            <InputTextField
              variant="outlined"
              inputProps={{
                maxLength: tagNameMaxLength,
                'data-testid': TestIds.TAG_NAME_INPUT,
              }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleTagNameChange(event.target.value)
              }
              onBlur={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleTagNameChange(event.target.value)
              }
              value={locationTag}
              error={
                !!inputBoxStatus &&
                inputBoxStatus !== 'changed' &&
                inputBoxStatus !== ''
              }
              helperText={inputBoxStatus === 'changed' ? '' : inputBoxStatus}
            />
          </InputContainer>
          <LocationList
            locations={viewLocations}
            isLoadingLocations={isLoadingLocations}
            tagId={Number(tagId)}
            handleUpdateLocations={handleUpdateLocations}
            totalPages={totalPages}
            totalCount={totalCount}
            page={page}
            handlePageChange={handlePageChange}
            locationsSelectedCount={locationsSelectedCount}
            setLocationsSelectedCount={setLocationsSelectedCount}
            setDelta={setDelta}
          />
        </MainPane>
      </TwoPaneLayout>

      <SimpleDialog
        open={CloseModal.open}
        cancelText={lang.cancel}
        confirmText={lang.confirm}
        title={lang.discardModal.title}
        onClose={CloseModal.onCloseModal}
        onClickCancel={CloseModal.onCloseModal}
        modalSize="sm"
        onClickConfirm={handleModalConfirm}
      />
      {!isViewOnly && (
        <Footer
          showFooterActions
          onCancel={CloseModal.onOpenModal}
          onSave={handleSave}
          cancelLabel={lang.close}
          disableSave={isSaveDisabled}
          isSaving={isSaving}
        />
      )}
    </Grid>
  );
};
